import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import { Wordmark } from "../components/Motion";
import { paymentStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling");
  const [txn, setTxn] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!sid) { setState("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const r = await paymentStatus(sid);
        if (r.payment_status === "paid") { setTxn(r); setState("paid"); clear(); return; }
        if (["expired", "failed"].includes(r.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      if (++tries < 8) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
    // eslint-disable-next-line
  }, [sid]);

  const isDonation = txn?.type === "donation";

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-8 py-24 sm:py-32 text-center min-h-[60vh]" data-testid="payment-success-page">
      {state === "polling" && (
        <div data-testid="payment-polling"><Loader2 className="animate-spin mx-auto text-graytx" size={36} /><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-graytx mt-6">Confirming your payment…</p></div>
      )}
      {state === "paid" && isDonation && (
        <div data-testid="payment-paid-donation">
          <Heart className="mx-auto text-cs-green" size={52} fill="currentColor" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cs-green mt-6">Donation received</p>
          <h1 className="font-display font-extrabold text-5xl sm:text-6xl uppercase mt-4 leading-[0.9]">Thank you<br />for backing us</h1>
          <p className="text-graytx mt-5 leading-relaxed">
            Your ${Number(txn.amount).toFixed(0)} donation just went straight into building Covertswag — the next drop, better fabrics, and an independent brand made possible by people like you. We won't forget it.
          </p>
          <div className="mt-10"><Wordmark className="text-2xl" /></div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Link to="/shop" data-testid="donation-shop-link" className="font-mono text-[11px] uppercase tracking-[0.2em] bg-cream text-bg px-8 py-4 hover:bg-cs-green hover:text-cream transition-colors duration-200">Shop the drop</Link>
            <Link to="/" data-testid="donation-home-link" className="font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-8 py-4 hover:bg-charcoal transition-colors duration-200">Back home</Link>
          </div>
        </div>
      )}
      {state === "paid" && !isDonation && (
        <div data-testid="payment-paid">
          <CheckCircle2 className="mx-auto text-cs-green" size={52} />
          <h1 className="font-display font-extrabold text-5xl sm:text-6xl uppercase mt-6 leading-[0.9]">You're in the drop</h1>
          <p className="text-graytx mt-5 leading-relaxed">
            Your pre-order is confirmed. It ships the week of Sep 15 and you'll get tracking by email. 8% of this order is on its way to fight the cold — thank you.
          </p>
          <div className="mt-10"><Wordmark className="text-2xl" /></div>
          <Link to="/" data-testid="success-home-link" className="inline-block mt-6 font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-8 py-4 hover:bg-charcoal transition-colors duration-200">Back home</Link>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error">
          <XCircle className="mx-auto text-cs-red" size={48} />
          <h1 className="font-display font-extrabold text-4xl uppercase mt-6">Couldn't confirm</h1>
          <p className="text-graytx mt-4">We couldn't confirm this payment. If you were charged, email hey@covertswag.com and we'll sort it out.</p>
          <Link to="/shop" className="inline-block mt-8 font-mono text-[11px] uppercase tracking-[0.2em] border border-line px-8 py-4 hover:bg-charcoal transition-colors duration-200">Back to shop</Link>
        </div>
      )}
    </div>
  );
}
